import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTheme, type Theme } from "../../contexts/ThemeContext";
import { SunIcon, MoonIcon, CheckIcon } from "../icons";
import styles from "./ThemeToggle.module.css";

interface ThemeOption {
  value: Theme;
  labelKey: string;
  icon: typeof SunIcon;
}

const themeOptions: ThemeOption[] = [
  { value: "light", labelKey: "theme.light", icon: SunIcon },
  { value: "dark", labelKey: "theme.dark", icon: MoonIcon },
  { value: "system", labelKey: "theme.system", icon: SunIcon },
];

export function ThemeToggle() {
  const { t } = useTranslation();
  const { theme, resolvedTheme, setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const handleThemeChange = (newTheme: Theme) => {
    setTheme(newTheme);
    setIsOpen(false);
  };

  const CurrentIcon = resolvedTheme === "dark" ? MoonIcon : SunIcon;

  return (
    <div className={styles.container} ref={menuRef}>
      <button
        className={styles.button}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={t("theme.toggle")}
        title={t("theme.toggle")}
      >
        <CurrentIcon className={styles.icon} />
      </button>

      {isOpen && (
        <div className={styles.dropdown} role="listbox">
          {themeOptions.map((option) => {
            const IconComponent = option.icon;
            const isActive = theme === option.value;
            return (
              <button
                key={option.value}
                className={`${styles.option} ${isActive ? styles.optionActive : ""}`}
                onClick={() => handleThemeChange(option.value)}
                role="option"
                aria-selected={isActive}
              >
                <IconComponent className={styles.optionIcon} />
                <span className={styles.optionLabel}>{t(option.labelKey)}</span>
                {isActive && <CheckIcon className={styles.checkmark} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
